import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

const inputSchema = z.object({
  lang: z.string().min(2).max(5),
  texts: z.array(z.string()).min(1).max(200),
});

const LANG_NAME: Record<string, string> = {
  en: "English",
  af: "Afrikaans",
  zu: "isiZulu (Zulu)",
  fr: "French",
  de: "German",
  es: "Spanish",
  pt: "Portuguese",
};

export const translateTexts = createServerFn({ method: "POST" })
  .inputValidator((data) => inputSchema.parse(data))
  .handler(async ({ data }) => {
    const { lang, texts } = data;
    if (lang === "en") return { translations: texts };

    const apiKey = process.env.LOVABLE_API_KEY;
    if (!apiKey) throw new Error("LOVABLE_API_KEY not configured");

    const targetName = LANG_NAME[lang] ?? lang;
    const sys = `You are a professional website localiser. Translate the given JSON array of UI strings from English into ${targetName}. Rules: preserve order and length of the array exactly; keep brand names ("BrandIn Studio", "WhatsApp", "Google", "SEO"), URLs, emails, phone numbers and prices (e.g. "R999", "R1,999") unchanged; keep punctuation and casing style; do not translate proper nouns; return ONLY a JSON object of shape {"t": string[]} with no commentary.`;

    const res = await fetch("https://ai.gateway.lovable.dev/v1/chat/completions", {
      method: "POST",
      headers: {
        Authorization: `Bearer ${apiKey}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        model: "google/gemini-2.5-flash-lite",
        messages: [
          { role: "system", content: sys },
          { role: "user", content: JSON.stringify(texts) },
        ],
        response_format: { type: "json_object" },
      }),
    });

    if (!res.ok) {
      const body = await res.text().catch(() => "");
      throw new Error(`AI gateway ${res.status}: ${body.slice(0, 200)}`);
    }
    const json = await res.json();
    const content: string = json?.choices?.[0]?.message?.content ?? "{}";
    let parsed: { t?: unknown };
    try {
      parsed = JSON.parse(content);
    } catch {
      parsed = {};
    }
    const arr = Array.isArray(parsed.t) ? (parsed.t as unknown[]) : [];
    const translations = texts.map((orig, i) => {
      const v = arr[i];
      return typeof v === "string" && v.trim() ? v : orig;
    });
    return { translations };
  });
